<script setup>
import { ref, defineEmits, watch, onMounted } from "vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useRouter, useRoute } from "vue-router";
import Swal from "sweetalert2";
import RegisterUser from "@/views/auth/domain/register";
import Choices from "choices.js";

const emit = defineEmits(["close"]);
const router = useRouter();
const route = useRoute();

// Variables reactivas
const activeStep = ref(0);
const name = ref("");
const lastName = ref("");
const email = ref("");
const telefonoNumero = ref("");
const rolId = ref(3);

const errors = ref([]);
const message = ref("");
const loading = ref(false);
const password = ref("");
const confirmPassword = ref("");

// Updated roles to match your DTO (using numbers for role_id)
const roles = ref([
  { id: 2, nombre: "Administrador" }, // Assuming 1 is for admin
  { id: 3, nombre: "Instructor" }, // Assuming 1 is for admin
  { id: 4, nombre: "Estudiante" },  // Assuming 2 is for regular user
]);

// Password mismatch watcher
watch([password, confirmPassword], () => {
  errors.value = errors.value.filter(error => error !== "Las contraseñas no coinciden.");
  if (password.value !== confirmPassword.value) {
    errors.value.push("Las contraseñas no coinciden.");
    invalidFields.value.confirmPassword = true;
  } else {
    invalidFields.value.confirmPassword = false;
  }
});

const invalidFields = ref({});

const validateForm = () => {
  errors.value = [];
  invalidFields.value = {};

  // Validate required fields
  if (!name.value.trim()) {
    errors.value.push("El nombre es obligatorio.");
    invalidFields.value.name = true;
  }
  if (!lastName.value.trim()) {
    errors.value.push("El apellido es obligatorio.");
    invalidFields.value.lastName = true;
  }
  if (!email.value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    errors.value.push("El correo electrónico no es válido.");
    invalidFields.value.email = true;
  }

  if (telefonoNumero.value && !telefonoNumero.value.match(/^[0-9]{8}$/)) {
    errors.value.push("El teléfono no es válido.");
    invalidFields.value.telefonoNumero = true;
  }

  if (!rolId.value && activeStep.value === 1) {
    errors.value.push("Debe seleccionar un rol");
    invalidFields.value.rolId = true;
  }

  if (activeStep.value === 2) {
    if (!password.value.trim()) {
      errors.value.push("La contraseña es obligatoria.");
      invalidFields.value.password = true;
    } else if (password.value.length < 6) {
      errors.value.push("La contraseña debe tener al menos 6 caracteres.");
      invalidFields.value.password = true;
    }

    if (password.value !== confirmPassword.value) {
      errors.value.push("Las contraseñas no coinciden.");
      invalidFields.value.confirmPassword = true;
    }
  }

  return errors.value.length === 0;
};

const nextStep = () => {
  if (!validateForm()) return;
  if (activeStep.value < 2) {
    activeStep.value += 1;
  }
};

const prevStep = () => {
  if (activeStep.value > 0) {
    activeStep.value -= 1;
  }
};

const handleSubmit = async () => {
  errors.value = [];
  message.value = "";
  loading.value = true;

  if (!validateForm()) {
    loading.value = false;
    return;
  }

  try {
    const fullName = `${name.value} ${lastName.value}`;

    // Call the updated RegisterUser function
    await RegisterUser(
      fullName,        // nombre (combined first + last name)
      email.value,     // email
      password.value,  // contrasena
      rolId.value,     // role_id
      telefonoNumero.value ? parseInt(telefonoNumero.value) : undefined // telefono (optional)
    );

    Swal.fire({
      icon: "success",
      title: "¡Usuario registrado exitosamente!",
      text: `El usuario ${email.value} ha sido creado.`,
      customClass: {
        confirmButton: "btn bg-gradient-success",
      },
      buttonsStyling: false,
    });

    emit("close");
    closeModal();
    location.reload();

  } catch (error) {
    console.error("Registration error:", error);
    let errorMessage = "Error en el registro de usuario";

    if (error.message.includes("correo")) {
      errorMessage = "El correo electrónico ya está registrado";
    }

    Swal.fire({
      icon: "error",
      title: "Error",
      text: errorMessage,
      customClass: {
        confirmButton: "btn bg-gradient-danger",
      },
      buttonsStyling: false,
    });
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  emit("close");
  message.value = "";
  errors.value = [];
  name.value = "";
  lastName.value = "";
  email.value = "";
  telefonoNumero.value = "";
  password.value = "";
  confirmPassword.value = "";

  if (route.query.from === "sidebar") {
    router.back();
  }
};

const getChoices = (id) => {
  if (document.getElementById(id)) {
    var element = document.getElementById(id);
    return new Choices(element, {
      searchEnabled: false,
      allowHTML: true,
    });
  }
};

onMounted(() => {
  getChoices("choices-category-edit");
  getChoices("choices-color-edit");
  getChoices("choices-currency-edit");

  if (document.getElementById("choices-tags-edit")) {
    var tags = document.getElementById("choices-tags-edit");
    const examples = new Choices(tags, {
      removeItemButton: true,
      allowHTML: true,
    });
  }
});

</script>
<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content card">
      <div class="row h-70 h-lg-100">
        <!-- Left card section with step icon -->
        <div class="col-lg-4 col-md-6 col-12">
          <div class="step-card d-flex flex-column justify-content-center align-items-center text-center">
            <div class="step-icon">
              <i class="far fa-id-card" :class="stepIconClass"></i>
            </div>

            <div class="progress mb-3 mt-4 w-100">
              <div class="progress-bar bg-gradient-dark" role="progressbar" :style="{ width: ((activeStep + 1) / 3) * 100 + '%' }"
                aria-valuenow="10" aria-valuemin="0" aria-valuemax="100"></div>
            </div>
            <p class="mb-0">{{ activeStep + 1 }} de 3</p>
          </div>
        </div>

        <!-- Right side with form -->
        <div class="col-lg-8 col-md-6 col-12">
          <div class="modal-header p-1">
            <h5 class="modal-title">Nuevo Usuario</h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>

          <form @submit.prevent="handleSubmit">
            <div class="modal-body">
              <!-- Step 1: Datos Personales -->
              <div v-if="activeStep === 0">
                <div class="row pe-3 list-row">
                  <div class="col-lg-1 col-12">
                    <i class="fas fa-portrait fa-lg me-2 mt-2"></i>
                  </div>
                  <div class="col-lg-4 col-12">
                    <input v-model="name" type="text" class="form-control mb-3"
                      :class="{ 'is-invalid': invalidFields.name }" placeholder="Nombre*" required />
                  </div>
                  <div class="col-lg-7 col-12">
                    <input v-model="lastName" type="text" class="form-control mb-3"
                      :class="{ 'is-invalid': invalidFields.lastName }" placeholder="Apellidos*" required />
                  </div>
                </div>

                <hr class="horizontal dark mt-1 mb-4" />

                <div class="row pe-3 list-row">
                  <div class="col-lg-1 col-12">
                    <i class="fas fa-phone-square-alt fa-lg me-2 mt-2"></i>
                  </div>
                  <div class="col-lg-6 col-12">
                    <input v-model="telefonoNumero" type="text" class="form-control mb-3"
                      :class="{ 'is-invalid': invalidFields.telefonoNumero }" placeholder="Número de Teléfono*" required />
                      <strong v-if="invalidFields.telefonoNumero" class="text-danger text-xs">*El numero de teléfono no es
                        válido</strong>
                  </div>
                </div>
                <div class="row pe-3 list-row">
                  <div class="col-lg-1 col-12">
                    <i class="fas fa-envelope me-2 fa-lg me-2 mt-2"></i>
                  </div>
                  <div class="col-lg-6 col-12">
                    <input v-model="email" type="text" class="form-control mb-3"
                      :class="{ 'is-invalid': invalidFields.email }" placeholder="Correo Electrónico*" required />
                    <strong v-if="invalidFields.email" class="text-danger text-xs">*El correo electrónico no es
                      válido</strong>
                  </div>
                </div>
              </div>

              <!-- Step 2: Permisos y Roles -->
              <div v-if="activeStep === 1">
                <div class="row pe-3 list-row icons justify-content-center">
                  <p class="mb-0 text-sm">Seleccione un rol:</p>
                </div>
                <div class="row pe-3 my-3 list-row icons justify-content-start">
                  <div class="col-5 mb-3 d-flex flex-column align-items-center" v-for="rol in roles" :key="rol.id">
                    <div class="d-md-flex align-items-center w-100">
                      <div class="role-option me-2" :class="{
                        selected: rolId === rol.id,
                        'is-invalid': invalidFields.rolId,
                      }" @click="rolId = rol.id">
                        <i class="fas" :class="rol.id === 2 ? 'fa-user-shield' : 'fa-user'"></i>
                      </div>
                      <span :class="{ 'active-label': rolId === rol.id }">{{ rol.nombre }}</span>
                    </div>
                  </div>
                </div>
              </div>


              <!-- Step 3: Contraseña -->
              <div v-if="activeStep === 2">
                <input v-model="password" type="password" class="form-control mb-3 w-80 w-md-50 pe-5" placeholder="Contraseña"
                  :class="{ 'is-invalid': invalidFields.password }" required />
                <div v-if="invalidFields.password" class="invalid-feedback text-xs mt-0">
                  Las contraseñas no cumple con los requisitos.
                </div>
                <input v-model="confirmPassword" type="password" class="form-control mb-3 w-80 w-md-50 pe-5"
                  placeholder="Confirmar Contraseña" :class="{ 'is-invalid': invalidFields.confirmPassword }"
                  required />
                <div v-if="invalidFields.confirmPassword" class="invalid-feedback text-xs mt-0">
                  Las contraseñas no coinciden.
                </div>
                <hr class="horizontal dark mt-4 mb-4" />

                <small class="text-muted"> *La contraseña debe contener: </small>
                <div class="row list-row w-100">
                  <div class="col-lg-6 col-12 ps-0">
                    <ul class="password-requirements">
                      <li>8 caracteres como mínimo</li>
                      <li>Una letra mayúscula.</li>
                      <li>Una letra minúscula.</li>
                    </ul>
                  </div>
                  <div class="col-lg-6 col-12 ps-0">
                    <ul class="password-requirements">
                      <li>Un número.</li>
                      <li>Un carácter especial (ej. !, @, #, etc.).</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <div class="d-flex justify-content-between w-100">
                <div class="col-lg-6 col-md-6 ps-0">
                  <small class="text-muted">
                    <i class="fas fa-asterisk text-dark fa-sm pe-1"></i>
                    Requerido
                  </small>
                </div>
                <div class="col-lg-6 col-7 ps-0 text-end">
                  <argon-button type="button" color="secondary" class="me-3" @click="prevStep"
                    v-if="activeStep > 0">Anterior</argon-button>
                  <argon-button type="button" color="dark" @click="nextStep"
                    v-if="activeStep < 2">Siguiente</argon-button>
                  <argon-button :disabled="loading" type="submit" color="dark" variant="gradient"
                    v-if="activeStep === 2">
                    {{ loading ? "Procesando..." : "Registrar" }}
                  </argon-button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.step-card {
  padding: 20px;
  background: #344767;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 0 30px rgba(115, 255, 0, 0.1);
  height: 100%;
}

.step-icon {
  font-size: 100px;
  color: #fff;
}

.step-card p {
  font-size: 1rem;
  color: #fff;
}

.list-row .col-lg-6 {
  margin-bottom: 15px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.list-row.icons {
  column-gap: 3rem;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 900px;
  height: 460px;
  width: 90%;
  padding: 20px;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  height: 330px;
  overflow-y: auto;
}

.modal-footer {
  height: 60px;
  gap: 10px;
  margin-top: auto;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
}

input.form-control:focus {
  border-bottom: 4px solid #4e4e4e;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-width: 400px;
    padding: 15px;
    height: 700px;
  }

  .modal-header {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .list-row.icons {
    column-gap: 1rem;
  }

  .modal-title {
    font-size: 1rem;
    margin-top: 15px;
  }

  .btn-close {
    font-size: 1.2rem;
    align-self: flex-end;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
    padding: 0%;
  }

  .argon-button {
    width: 100%;
  }

  .step-icon {
    font-size: 30px;
    color: #fff;
  }

  .step-card {
    flex-direction: row !important;
    justify-content: space-between !important;
    padding-top: 0%;
    padding-bottom: 0%;
  }

  .progress {
    width: 50% !important;
    margin-top: 1rem !important;
    margin-bottom: 1rem !important;

  }

  .modal-body {
    height: 410px;
    overflow-y: auto;
  }

  .role-option {
    margin-top: 10px !important;
    width: 60px !important;
    height: 60px !important;
  }


}

.role-option {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  margin: 10px;
  margin-top: 0px;
  margin-bottom: 3px;
  font-size: 24px;
  position: relative;
}

.role-option i {
  color: #555;
}

.role-labels {
  display: flex;
  justify-content: center;
  gap: 4rem;
  width: 100%;
}

.role-labels span {
  font-size: 14px;
  color: #777;
}

.role-option.selected {
  border: 3px solid #344767;
  background-color: rgba(45, 206, 137, 0.1);
}

.active-label {
  font-weight: bold;
  color: #2dce89;
}

.password-requirements {
  list-style-type: none;
  padding-left: 25px;
  margin-top: 2px;
}

.password-requirements li {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
}

.password-requirements li::before {
  content: "•";
  color: #2dce89;
  font-size: 1.2rem;
  margin-right: 8px;
  display: inline-block;
}

.is-invalid {
  border-bottom: 2px solid red !important;
  background: linear-gradient(to top, #faecec, #ffffff);
  transition:
    0.3s ease-in-out,
    border 0.3s ease-in-out;
}
</style>
